import React, {useEffect, useState, useRef, useCallback} from "react";
import {Button, DatePicker, Typography, Spin, Tooltip} from "antd";
import dayjs from "dayjs";
import PropTypes from "prop-types";

import EditableTable from "../../../components/TripLogEditableTable";
import {getCarBookings} from "../../../helpers/car";
import {batchUpdate, getDistanceUnit} from "../../../helpers/booking";
import {messageType, showMessages} from "../../../helpers/message";
import {dayNameDateTimeFormat} from "../../../lib/time";
import {useAppDispatch} from '../../../hooks/useStore';
import {apiSlice} from '../../../store/api/apiSlice';

import "./TripLogEntry.less";
import {InfoCircleOutlined} from "@ant-design/icons";

const {MonthPicker} = DatePicker;
const {Text} = Typography;

const TripLogEntry = ({carID}) => {
    const abortControllerRef = useRef(new AbortController());
    const abortSignalRef = useRef(abortControllerRef.current.signal);
    const dispatch = useAppDispatch();

    const [loading, setLoading] = useState(false);

    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const [totalRows, setTotalRows] = useState(10);

    const [bookingsData, setBookingsData] = useState(undefined);
    const [initialDistances, setInitialDistances] = useState(undefined);
    const [bookingsDistance, setBookingsDistance] = useState(undefined);
    const [month, setMonth] = useState(dayjs().subtract(1, 'month').startOf('month'));

    //region UI helpers

    const onMonthChange = (dateString) => {
        const newMonth = dayjs(dateString).startOf('month');

        if (!month.isSame(newMonth)) {
            setMonth(newMonth);
            fetchBookings(newMonth, 1, perPage);
        }
    };

    const disabledMonth = (current) => {
        return current && current.startOf('month') > dayjs();
    };

    const onPageChange = (page, perPage) => {
        setPage(page);
        setPerPage(perPage);
        fetchBookings(month, page, perPage);
    };

    //endregion

    //region API requests

    const fetchBookings = useCallback(async (month = null, page = 1, perPage = 10) => {
        const selectedMonth = month || dayjs().subtract(1, 'month');

        const startedAt = selectedMonth.startOf('month').format('YYYY-MM-DD HH:mm:ssZZ');
        const endedAt = selectedMonth.endOf('month').format('YYYY-MM-DD HH:mm:ssZZ');

        setLoading(true);

        try {
            const abortSignal = abortSignalRef.current;
            const response = await getCarBookings(carID, startedAt, endedAt, page, perPage, abortSignal);

            if (response) {
                const bookings = response.data;

                const data = getBookingsDataForTable(bookings);
                const distanceData = getDistanceData(data);

                setBookingsData(data);
                setInitialDistances(distanceData);
                setTotalRows(response.total);
                setPerPage(response.per_page);
                setBookingsDistance(undefined);
                setLoading(false);
            }
        } catch (error) {
            showMessages(error.message, messageType.error);
            setLoading(false);
            if(error.status_code === 404) window.location.replace('/404');
        }
    },[carID]);

    const onSubmit = async () => {
        if (bookingsDistance && bookingsDistance.length !== 0) {
            try {
                setLoading(true);
                const abortSignal = abortSignalRef.current;
                const response = await batchUpdate(bookingsDistance, abortSignal);

                if (response) {
                    // Temp invalidate Booking until refactored to use RTK batchUpdate.
                    dispatch(apiSlice.util.invalidateTags(bookingsDistance.map(booking => {
                        return {type: 'Booking', id: booking.id}
                    })));
                    await fetchBookings(month, page);
                    showMessages('Trip logs have been updated.', messageType.success);
                }
            } catch (error) {
                showMessages(error.message, messageType.error);
                setLoading(false);
            }
        } else {
            showMessages('Nothing to update!', messageType.error);
        }
    };

    //endregion

    //region helpers

    // Format data to feed to editable table
    const getBookingsDataForTable = (bookingDeployments) => {
        return bookingDeployments.map((bookingDeployment, index) => {
            const bookingData = {
                key: index.toString(),  // type string is expected
                bookingID: bookingDeployment.booking_id,
                startTime: dayjs(bookingDeployment.started_at).format(dayNameDateTimeFormat),
                endTime: dayjs(bookingDeployment.ended_at).format(dayNameDateTimeFormat),
            };
            const membership = bookingDeployment.booking.membership;
            // If the membership has been deleted default to this.
            if (membership === null) {
                bookingData.driverName = 'N/A';
                bookingData.driverNumber = 'N/A';
            } else {
                const membershipNumber = membership.membership_number;
                const accountNumber = membership.account.account_number;
                const person = membership.profile.person;
                const driverName = `${person.first_name} ${person.last_name}`;
                const driverNumber = `${accountNumber}-${membershipNumber}`;
                bookingData.driverName = driverName;
                bookingData.driverNumber = driverNumber;
            }

            bookingData.kmStart = null;
            bookingData.kmEnd = null;
            bookingData.distance = bookingDeployment.booking.booking_distance ? bookingDeployment.booking.booking_distance.distance : null;

            return bookingData;
        })
    };

    // Get bookingID with not null distances
    const getDistanceData = (bookingsData) => {
        let distancesData;

        //Filter out data without distance value
        const filteredArray = bookingsData.filter(booking => booking.distance !== null);

        //Only keep bookingID and distance
        distancesData = filteredArray.map(booking => {
            return {id: booking.bookingID, distance: booking.distance};
        });

        return distancesData;
    };

    // Filter out un-modified distance
    const filterModifiedDistance = (distancesData) => {
        let result = distancesData;
        const existingDistances = initialDistances;

        result = result.filter(object => {
            let id = object.id;
            let distance = object.distance;

            let distances = existingDistances.filter(element => element.id === id && element.distance === distance);

            return distances.length === 0;
        });

        return result;
    };

    const updateDistanceState = (data) => {
        const distanceData = getDistanceData(data);
        const modifiedDistances = filterModifiedDistance(distanceData);

        setBookingsDistance(filterModifiedDistance(modifiedDistances));
    };

    //endregion

    // onMount function.
    useEffect(() => {
        fetchBookings();
    }, [fetchBookings]);

    // onUnmount cleanup function.
    useEffect(() => {
        const abortController = abortControllerRef.current;
        return function abort() {
            abortController.abort();
        }
    }, []);

    const paginationAttr = {
        current: page,
        pageSize: perPage,
        total: totalRows,
        onChange: onPageChange,
        showSizeChanger: true,
        simple: window.innerWidth <= 767,
        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
        onShowSizeChange: onPageChange,
    };

    const currentMonth = month.startOf('month');
    const buttonDisabled = bookingsDistance === undefined || (bookingsDistance && bookingsDistance.length === 0);
    const distanceUnit = getDistanceUnit().toLowerCase();

    const odometerStartTitle =
        <span><Text strong>Odometer Start ({distanceUnit})</Text> <Tooltip
            title="If you don’t know the total distance for a trip, please enter the odometer start and odometer end values, and we will calculate the total trip distance for you!"
        > <InfoCircleOutlined/></Tooltip></span>;

    const odometerEndTitle =
        <span><Text strong>Odometer End ({distanceUnit})</Text> <Tooltip
            title="If you don’t know the total distance for a trip, please enter the odometer start and odometer end values, and we will calculate the total trip distance for you!"
        > <InfoCircleOutlined/></Tooltip></span>;

    const distanceTitle =
        <span><Text strong>Distance ({distanceUnit})</Text> <Tooltip
            title="If you do know the total distance for a trip, please enter the value here. Otherwise, please enter the odometer start and odometer end values, and we will calculate the total trip distance for you!"
        > <InfoCircleOutlined/></Tooltip></span>;

    const tableColumns = [
        {
            title: <Text strong>Driver #</Text>,
            dataIndex: 'driverNumber',
            width: '7.5%',
        },
        {
            title: <Text strong>Driver Name</Text>,
            dataIndex: 'driverName',
            width: '10%',
        },
        {
            title: <Text strong>Trip Start</Text>,
            dataIndex: 'startTime',
        },
        {
            title: <Text strong>Trip End</Text>,
            dataIndex: 'endTime',
        },
        {
            title: odometerStartTitle,
            dataIndex: 'kmStart',
            displayName: 'Odometer Start',
            editable: true,
            width: 200,
        },
        {
            title: odometerEndTitle,
            dataIndex: 'kmEnd',
            displayName: 'Odometer End',
            editable: true,
            width: 200,
        },
        {
            title: distanceTitle,
            dataIndex: 'distance',
            displayName: 'Distance',
            editable: true,
            width: 190,
        },
    ];

    return (
        <>
            <MonthPicker
                allowClear={false}
                placeholder="Select month"
                onChange={onMonthChange}
                value={currentMonth}
                defaultValue={currentMonth}
                disabledDate={disabledMonth}
                format="MMM YYYY"
            />
            <br/><br/>
            <Spin spinning={loading}>
                <EditableTable
                    columns={tableColumns}
                    data={bookingsData}
                    updateDistanceData={updateDistanceState}
                    pagination={paginationAttr}
                />
            </Spin>
            <Button
                type="primary"
                className='submit-button'
                onClick={onSubmit}
                loading={loading}
                disabled={buttonDisabled}
            >
                Update Changes
            </Button>
        </>
    )
}

export default TripLogEntry;

TripLogEntry.propTypes = {
    carID: PropTypes.number.isRequired
};
